import { notification } from "antd";
import { useEffect } from "react";
import useSWR, { SWRConfiguration } from "swr";
import { cache } from "swr/_internal";

interface UseSwrManagementProps<TResponse> {
  swrKey: string;
  fetcher: () => Promise<TResponse>;
  swrConfig?: SWRConfiguration;
  disableNotification?: boolean;
}

export const useSwrManagement = <TResponse>({
  swrKey,
  fetcher,
  swrConfig,
  disableNotification = false,
}: UseSwrManagementProps<TResponse>) => {
  const { data, isValidating, error } = useSWR(swrKey, fetcher, {
    revalidateOnFocus: false,
    errorRetryCount: 3,
    errorRetryInterval: 5000,
    ...swrConfig,
  });

  useEffect(() => {
    if (error && !disableNotification) {
      notification.error({
        message: "Error",
        description: "",
      });
    }
  }, [swrKey, error, disableNotification]);

  return {
    data: data ? (data as TResponse) : undefined,
    loading: isValidating,
    error,
  };
};

export const useClearCache = () => {
  const clearCache = (keys: string[]) => {
    const cacheKeys = [...cache.keys()];

    cacheKeys
      .filter((cacheKey) => keys.some((key) => cacheKey.startsWith(key)))
      .forEach((cacheKey) => {
        cache.delete(cacheKey);
      });
  };

  return { clearCache };
};
